import { ApolloClient, ApolloLink, from, HttpLink } from '@apollo/client'

import { MY_WORKSPACES } from '../data/queries/workspace'
import type { UsersWorkspace } from '../types/user'
import { getWorkspaceNameFromUrl } from '../utils/get-workspace-from-url'
import { tokenUtils } from '../utils/token-utils'
import { currentWorkspaceVar } from '../utils/update-workspace-headers'
import { cache } from './apollo-cache'
import { envConfig } from './env-config'
import { errorLink } from './error-link'

interface CustomHeaders {
  authorization: string
  handle?: string
  'ws-id'?: string
}

const httpLink = new HttpLink({
  uri: envConfig.graphqlUrl
})

const authMiddleware = new ApolloLink((operation, forward) => {
  // Get the headers
  const { headers = {} } = operation.getContext()

  // Get the authentication token
  const token = tokenUtils.getToken('Access-Token')

  // Initialize headers
  const customHeaders: CustomHeaders = {
    authorization: token || ''
  }

  // Only run on client-side
  if (typeof window !== 'undefined') {
    // Check if ws-id is already provided in the operation context (highest priority)
    const contextWorkspaceId = headers['ws-id'] || headers['Ws-Id']

    if (contextWorkspaceId) {
      // Use the workspace ID from the operation context
      customHeaders['ws-id'] = contextWorkspaceId
    } else {
      // Try to get workspace ID from reactive variable
      let workspaceId = currentWorkspaceVar()

      // If reactive variable is not set or might be stale, try to get it from URL
      // This is a safeguard against race conditions during workspace switching
      if (!workspaceId) {
        const workspaceNameFromUrl = getWorkspaceNameFromUrl()
        if (workspaceNameFromUrl) {
          try {
            // Try to read workspace ID from Apollo cache
            const cachedData = cache.readQuery<{ myWorkspaces: UsersWorkspace[] }>({
              query: MY_WORKSPACES
            })
            if (cachedData?.myWorkspaces) {
              const urlWorkspace = cachedData.myWorkspaces.find(
                (ws: UsersWorkspace) => ws.workspace?.workspacename === workspaceNameFromUrl
              )
              if (urlWorkspace?.workspace?.id) {
                workspaceId = urlWorkspace.workspace.id
                // Update reactive variable to keep it in sync
                currentWorkspaceVar(workspaceId)
              }
            }
          } catch (error) {
            // Cache read failed, continue with reactive variable value (which might be null)
          }
        }
      }

      if (workspaceId) {
        customHeaders['ws-id'] = workspaceId
      }
    }
  }

  // Set the headers
  operation.setContext({
    headers: {
      ...headers,
      ...customHeaders
    }
  })

  return forward(operation)
})

// Create the Apollo Client
export const client = new ApolloClient({
  cache: cache,
  connectToDevTools: true,
  link: from([errorLink, authMiddleware, httpLink])
})
